<template>
  <div class="popup">
    <div class="popup_cover" @click.self="confirmClosePop"></div>
    <div class="file-upload-popup" ref="popupRef">
      <div class="popup-header" @mousedown="startDrag">
        <div class="header_icon_box">
          <img src="@/assets/icon/note_icon2.svg" class="note_icon" />
          <div class="popup_title">관리자 공고 메모</div>
        </div>
        <img src="@/assets/images/close_btn.png" @click="closePop" class="close_icon" />
      </div>
      <div class="popup-body">
        <div class="contents_body" ref="contentsBodyRef">
          <div class="contents_box" v-for="(memo, index) in memoDatas" :key="index">
            <div class="content_header">
              <div class="content_header_left">
                <img v-if="memo.회원 == null" src="@/assets/icon/PTBID_icon.svg" class="content_icon" :class="{ 'with-new': isUnreadMemo(memo) }" />
                <div v-if="memo.회원 != null" class="content_icon" :class="{ 'with-new': isUnreadMemo(memo) }">{{ memo?.회원?.사업자명 }}</div>
                <div class="new_mark" v-if="isUnreadMemo(memo)">NEW</div>
                <div class="content_date">{{ memo?.작성일시날짜 }}</div>
                <div class="content_line"></div>
                <div class="content_time">{{ memo?.작성일시시간 }}</div>
              </div>
              <!-- <div class="content_header_right">
                <div class="content_button">수정</div>
                <div class="content_button">삭제</div>
              </div> -->
            </div>
            <div class="content_body">
              <div class="content_text" v-html="formatContent(memo?.내용)"></div>
              <!-- <textarea class="content_text" v-html="memo.내용"></textarea> -->
            </div>
          </div>
        </div>
      </div>
      <div class="popup-footer">
        <textarea class="text_box" placeholder="내용을 입력하세요." v-model="text" @input="updateCharacterCount" maxlength="200" name="" id=""></textarea>
        <div class="footer_info_box">
          <div class="text_count_box">
            <div>{{ charCount }}</div>
            <div>/</div>
            <div>200</div>
          </div>
          <div class="text_button" :disabled="charCount === 0" @click="addMemo">작성</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, defineEmits, defineProps, onMounted, nextTick } from "vue";
import announceAPI from "../../api/announceAPI";
import { useRouter } from "vue-router";
import { ApiResponse, Announce, Memo } from "../../common/types";
// 부모로부터 closePopup 이벤트를 수신
const emits = defineEmits<{
  (e: "closeNotepop"): void;
  (e: "getDetail"): void;
}>();

// 닫기 버튼 핸들러
const closePop = () => {
  emits("closeNotepop");
  emits("getDetail");
};
// 팝업 위치 및 드래그 상태 관리
const popupRef = ref<HTMLDivElement | null>(null);
const offset = ref({ x: 0, y: 0 });
const isDragging = ref(false);
const router = useRouter();
// 드래그 시작
const startDrag = (event: MouseEvent) => {
  if (!popupRef.value) return;

  isDragging.value = true;
  const rect = popupRef.value.getBoundingClientRect();

  // 클릭 위치와 팝업 위치 간의 거리 계산
  offset.value.x = event.clientX - rect.left;
  offset.value.y = event.clientY - rect.top;

  // 마우스 이동 및 릴리스 이벤트 등록
  document.addEventListener("mousemove", drag);
  document.addEventListener("mouseup", stopDrag);
};

// 드래그
const drag = (event: MouseEvent) => {
  if (!isDragging.value || !popupRef.value) return;

  const x = event.clientX - offset.value.x;
  const y = event.clientY - offset.value.y;

  // 팝업 위치 변경
  popupRef.value.style.left = `${x}px`;
  popupRef.value.style.top = `${y}px`;
};

// 드래그 종료
const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener("mousemove", drag);
  document.removeEventListener("mouseup", stopDrag);
};
// 상태 변수 선언
const text = ref(""); // textarea의 값
const charCount = ref(0); // 현재 글자 수
// 글자 수 업데이트 함수
const updateCharacterCount = (event: Event) => {
  // 이벤트 객체로 글자 길이를 계산
  const target = event.target as HTMLTextAreaElement;
  charCount.value = target.value.length;
};

// 메모 추가 API 호출
const addMemo = async () => {
  if (!text.value.trim()) return;
  // 사용자 확인을 위한 confirm 메시지
  const confirmed = confirm("작성한 메모를 등록하시겠습니까?");
  if (!confirmed) return;

  const payload = {
    공고순번: Number(router.currentRoute.value.params.seq),
    내용: text.value,
  };

  try {
    const res = await announceAPI.addMemo(payload);
    if (res.data.success) {
      memoDatas.value = res.data.data;
      text.value = "";
      charCount.value = 0;
      await nextTick();
      scrollToBottom();
    } else {
      alert("메모 추가에 실패했습니다.");
    }
  } catch (error) {
    console.error(error);
    alert("메모 추가 중 오류가 발생했습니다.");
  }
};

//메모 개별조회
const memoDatas = ref<Memo[]>([]);
const contentsBodyRef = ref<HTMLDivElement | null>(null);
const scrollToBottom = () => {
  if (contentsBodyRef.value) {
    setTimeout(() => {
      contentsBodyRef.value!.scrollTop = contentsBodyRef.value!.scrollHeight;
    }, 10); // DOM 업데이트 이후 실행
  }
};
const isUnreadMemo = (memo: Memo) => {
  const isContractor = true; // 역할을 확인하는 실제 로직으로 교체
  if (isContractor) {
    return memo?.수주회원확인여부 === false;
  } else {
    return memo?.수행회원확인여부 === false;
  }
};
const formatContent = (content: string | undefined): string => {
  if (!content) return ""; // null/undefined 방지
  return content.replace(/\n/g, "<br>");
};
// 메모 데이터를 조회하고 스크롤 제어
const noteDetail = async () => {
  const seq = Number(router.currentRoute.value.params.seq);

  try {
    const res = await announceAPI.memoDetail(seq);
    if (res.data.data) {
      memoDatas.value = res.data.data;

      // DOM 업데이트 후 스크롤을 제일 아래로 이동
      await nextTick();
      scrollToBottom();
    }
  } catch (error) {
    console.error("메모 데이터를 가져오는 중 오류 발생:", error);
  }
};
const announceData = ref({} as Announce);
const props = defineProps<{
  announceData: Announce;
}>();

// 팝업 닫기 전에 확인
const confirmClosePop = () => {
  if (text.value.trim()) {
    const confirmed = confirm("작성 중인 메모가 있습니다. 정말 닫으시겠습니까?");
    if (!confirmed) {
      return; // 사용자가 취소하면 닫지 않음
    }
  }
  closePop(); // 작성 중이 아니거나 사용자가 확인하면 팝업 닫기
};
onMounted(() => {
  announceData.value = JSON.parse(JSON.stringify(props.announceData));
  noteDetail();
});
</script>

<style scoped>
.popup {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0px;
  left: 0px;
}
.popup_cover {
  width: 100%;
  height: 100%;
  background-color: black;
  opacity: 0.5;
}
.file-upload-popup {
  /* Add your styles here */
  position: fixed;
  background-color: white;
  width: 832px;
  height: 846px;
  background: #f6f7fb;
  top: calc(50% - 423px);
  left: calc(50% - 416px);
  border-radius: 11px;
}
.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
  padding: 27px 30px 0px 40px;
  cursor: move;
}
.popup_title {
  font-size: 18px;
  font-family: "Noto Sans CJK KR-Bold";
  letter-spacing: -0.05em;
  color: black;
}
.popup-body {
  padding: 20px 40px;
}
.popup-footer {
  display: flex;
  flex-direction: column;
  padding: 0px 40px;
}
.popup_confirm_btn {
  width: 132px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #282b33;
  border-radius: 6px;
  font-family: "Noto Sans CJK KR-Bold";
  color: white;
  font-size: 14px;
  cursor: pointer;
}
.close_icon {
  cursor: pointer;
  width: 20px;
  height: 20px;
}
.contents_body {
  display: flex;
  flex-direction: column;
  height: 550px;
  gap: 12px;
  overflow-y: auto;
}
.contents_div {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px solid #eeeeee;
  padding: 20px 15px;
  justify-content: center;
  cursor: pointer;
}
/*  */
.contents_div {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}

/* 선택되지 않은 항목에만 hover 스타일 적용 */
.contents_div:not(.selected):hover {
  background-color: #f0f0f0;
}

/* 선택된 항목 스타일 */
.selected {
  background-color: #222957;
  color: white;
  font-weight: bold;
}

.popup_confirm_btn {
  cursor: pointer;
}

.close_icon {
  cursor: pointer;
}
.header_icon_box {
  display: flex;
  align-items: center;
  gap: 10px;
}
.note_icon {
  width: 30px;
  height: 30px;
}
.contents_box {
  padding: 25px 24px 20px 23px;
  background-color: #fff;
  border-radius: 14px;
  min-height: auto;
}
.content_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dbdbdb;
  padding-bottom: 11px;
}
.content_header_left {
  display: flex;
  align-items: center;
}
.content_header_right {
  display: flex;
  gap: 10px;
  width: 134px;
}
.content_button {
  width: 62px;
  height: 28px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9e9e9;
  border-radius: 6px;
  cursor: pointer;
  font-family: "Noto Sans CJK KR-Medium";
  font-size: 13px;
}
.content_icon {
  width: 116px;
  height: 21px;
  margin-right: 21px;
  font-size: 16px;
  font-family: "Noto Sans CJK KR-Bold";
  color: #3382e9;
}
.content_icon.with-new {
  margin-right: 5px;
}
.content_date {
  font-family: "Noto Sans CJK KR-Medium";
  color: #646464;
  font-size: 12px;
  margin-right: 10px;
}
.content_line {
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid #9e9e9e;
  width: 1px;
  height: 10px;
}
.content_time {
  font-family: "Noto Sans CJK KR-Medium";
  color: #646464;
  font-size: 12px;
  margin-left: 10px;
}
.content_body {
  padding: 15px 0px 0px 0px;
}
.content_text {
  font-family: "Noto Sans CJK KR-Medium";
  color: #272727;
  font-size: 14px;
  height: auto;
}
.text_box {
  border: 1px solid #e7e7e7;
  resize: none;
  overflow: auto;
  height: 80px;
  border-bottom: none;
  padding: 8px 12px 0px 12px;
}
.footer_info_box {
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 20px 10px 20px;
  border-left: 1px solid #e7e7e7;
  border-right: 1px solid #e7e7e7;
  border-bottom: 1px solid #e7e7e7;
}
.text_count_box {
  display: flex;
  font-size: 13px;
}
.text_count_box > div:nth-child(2),
.text_count_box > div:nth-child(3) {
  color: #999999;
}
.text_button {
  border: 1px solid #e7e7e7;
  width: 62px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
}
.new_mark {
  border: 1px solid #ff5a5a;
  width: 32px;
  height: 14px;
  font-size: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background-color: #ff5a5a;
  color: #ffffff;
  margin-right: 10px;
}
</style>
